/**
 * Format number with commas
 */
export const formatNumber = num => {
  if (!num && num !== 0) return '-';
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * Format file size in bytes to MB
 */
export const formatFileSize = bytes => {
  if (!bytes) return '0 MB';
  const mb = (bytes / (1024 * 1024)).toFixed(2);
  return `${mb} MB`;
};

/**
 * Format milliseconds to seconds
 */
export const formatMilliseconds = ms => {
  if (!ms && ms !== 0) return '-';
  return `${ms.toLocaleString()} ms`;
};

/**
 * Format percentage
 */
export const formatPercentage = (value, decimals = 2) => {
  if (!value && value !== 0) return '0%';
  return `${parseFloat(value).toFixed(decimals)}%`;
};

/**
 * Format date to readable string
 */
export const formatDate = dateString => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format date and time
 */
export const formatDateTime = dateString => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Calculate percentage increase/decrease
 */
export const calculatePercentageChange = (current, previous) => {
  if (!previous || previous === 0) return 0;
  return (((current - previous) / previous) * 100).toFixed(2);
};

/**
 * Get color for performance metric
 */
export const getPerformanceColor = ms => {
  if (ms < 2000) return '#00A600'; // Green
  if (ms < 5000) return '#FF9900'; // Yellow/Orange
  return '#CC0000'; // Red
};

/**
 * Get color for percentage change
 */
export const getChangeColor = percentage => {
  if (percentage > 0) return '#00A600'; // Green for positive
  if (percentage < 0) return '#CC0000'; // Red for negative
  return '#666666'; // Gray for no change
};

/**
 * Format hour (0-23) to readable time
 */
export const formatHour = hour => {
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:00 ${period}`;
};

/**
 * Get default date range (last 7 days)
 */
export const getDefaultDateRange = () => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 7);

  // Format in local timezone to avoid UTC conversion
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    startDate: formatLocalDate(startDate),
    endDate: formatLocalDate(endDate)
  };
};

/**
 * Get Black Friday date for a given year
 * Black Friday is the day after Thanksgiving (4th Thursday of November)
 */
export const getBlackFridayDate = year => {
  // Start with November 1st
  let day = 1;
  let thursdayCount = 0;
  
  // Loop through November to find the 4th Thursday
  while (day <= 30) {
    const date = new Date(year, 10, day); // Month 10 = November
    
    // Check if this day is a Thursday (day 4)
    if (date.getDay() === 4) {
      thursdayCount++;
      
      // If this is the 4th Thursday (Thanksgiving), Black Friday is the next day
      if (thursdayCount === 4) {
        return new Date(year, 10, day + 1);
      }
    }
    day++;
  }
  
  // Fallback (shouldn't reach here)
  return new Date(year, 10, 29);
};

/**
 * Sort data by key
 */
export const sortData = (data, key, direction = 'asc') => {
  return [...data].sort((a, b) => {
    if (direction === 'asc') {
      return a[key] > b[key] ? 1 : -1;
    }
    return a[key] < b[key] ? 1 : -1;
  });
};
